import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { systemAlertsService } from '@/services/systemAlertsService';
import { useAuth } from '@/contexts/AuthContext';
import type { SystemAlert } from '@/types/systemAlerts';

const POLLING_INTERVAL_MS = 30_000;

export function useSystemAlerts() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const isAdmin = user?.role === 'admin';

  const query = useQuery<SystemAlert[]>({
    queryKey: ['systemAlerts', 'active'],
    queryFn: () => systemAlertsService.getActive(),
    refetchInterval: POLLING_INTERVAL_MS,
    enabled: isAdmin,
    staleTime: 10_000,
  });

  const acknowledgeMutation = useMutation({
    mutationFn: (id: number) => systemAlertsService.acknowledge(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['systemAlerts', 'active'] });
    },
  });

  const alerts = query.data || [];
  const criticalCount = alerts.filter((a) => a.severity === 'critical' && a.is_active).length;
  const warningCount = alerts.filter((a) => a.severity === 'warning' && a.is_active).length;

  return {
    alerts,
    criticalCount,
    warningCount,
    totalCount: criticalCount + warningCount,
    isLoading: query.isLoading,
    acknowledge: acknowledgeMutation.mutate,
  };
}
